import ShopSection from './components/ShopSection'
import { useState } from 'react'
import './App.css'
import NewsSection from './NewsSection'
import SEO from './components/SEO'

function App() {
  const [activeTab, setActiveTab] = useState('home')

  // Navigation items
  const navItems: Array<{id: string; label: string}> = []

  // Other destinations for cross-linking
  const destinations = [
    { name: 'HelloLasa', url: 'https://hellolasa.pages.dev', emoji: '🕌', description: 'Tibetan Cultural Capital' },
    { name: 'HelloYichang', url: 'https://helloyichang.pages.dev', emoji: '🌊', description: 'Three Gorges Gateway' },
    { name: 'HelloXizang', url: 'https://helloxizang.pages.dev', emoji: '🗻', description: 'Roof of the World' },
    { name: 'Wudang Time Chamber', url: 'https://wudangtimechamber.com', emoji: '🧘', description: 'Sacred Mountain Retreat for Men' }
  ]

  // Main attractions for Xinjiang
  const attractions = [
    {
      id: 1,
      title: 'Tian Shan Mountains',
      description: 'The Heavenly Mountains stretching across Xinjiang, offering breathtaking scenery and outdoor adventures.',
      image: '🏔️',
      features: ['Hiking trails', 'Snow-capped peaks', 'Alpine lakes', 'Wildlife viewing']
    },
    {
      id: 2,
      title: 'Kanas Lake',
      description: 'A stunning alpine lake known for its mysterious changing colors and pristine natural beauty.',
      image: '🌊',
      features: ['Boat tours', 'Hiking', 'Photography', 'Local Tuvan culture']
    },
    {
      id: 3,
      title: 'Turpan Depression',
      description: 'One of the lowest and hottest places in China, famous for its grapes and ancient history.',
      image: '🏜️',
      features: ['Grape valleys', 'Ancient ruins', 'Flaming Mountains', 'Local vineyards']
    },
    {
      id: 4,
      title: 'Kashgar Old City',
      description: 'A historic Silk Road city with rich Uyghur culture and vibrant bazaars.',
      image: '🕌',
      features: ['Sunday Market', 'Id Kah Mosque', 'Traditional crafts', 'Local cuisine']
    }
  ]

  // Travel tips
  const travelTips = [
    {
      title: 'Best Time to Visit',
      description: 'May to October offers pleasant weather for exploring Xinjiang\'s diverse landscapes.',
      icon: '📅'
    },
    {
      title: 'Local Cuisine',
      description: 'Try hand-pulled noodles, lamb kebabs, naan bread, and sweet Xinjiang grapes.',
      icon: '🍜'
    },
    {
      title: 'Cultural Respect',
      description: 'Respect local customs and traditions, especially when visiting religious sites.',
      icon: '🤝'
    },
    {
      title: 'Travel Documents',
      description: 'Ensure proper permits for border areas and carry identification at all times.',
      icon: '📋'
    }
  ]

  return (
    <>
      <SEO 
        title="Discover the Beauty of Xinjiang"
        description="Your comprehensive guide to Xinjiang tourism, attractions, culture, and travel tips. Explore Tian Shan Mountains, Kanas Lake, Turpan Depression and more."
        keywords="Xinjiang, travel, tourism, China, attractions,新疆,旅游,景点,天山,喀纳斯"
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Google-style Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg font-bold">XJ</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">HelloXinjiang</h1>
                <p className="text-gray-600 text-sm">Discover the Beauty of Xinjiang</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                    activeTab === item.id
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Contact Button */}
            
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-blue-500">📍</span>
              <span className="text-sm font-medium">Northwest China</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Discover the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Beauty</span><br />
              of <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Xinjiang</span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-10 max-w-2xl">
              Experience the stunning landscapes, rich cultures, and unforgettable adventures 
              in China's largest autonomous region. From snow-capped mountains to vast deserts, 
              Xinjiang offers a journey like no other.
            </p>

            
          </div>
        </div>
      </section>

      <ShopSection />

      {/* Free Tickets Promotion */}
      <section className="py-12 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-6">
              <span className="text-white text-2xl">🎫</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Free <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Xinjiang Attraction Tickets</span>
            </h2>
            <p className="text-xl text-gray-700 mb-6 max-w-2xl mx-auto">
              Get up to 5 attraction tickets per person by sending an email
            </p>
            <div className="bg-white rounded-xl p-6 shadow-lg inline-block">
              <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
                <div className="text-3xl">📧</div>
                <div className="text-left">
                  <p className="text-gray-800 font-medium">Email to Get Tickets</p>
                  <a 
                    href="mailto:info@helloxinjiang.com" 
                    className="text-lg font-semibold text-blue-600 hover:text-blue-800 transition-colors duration-200"
                  >
                    info@helloxinjiang.com
                  </a>
                </div>
                <a 
                  href="mailto:info@helloxinjiang.com"
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md"
                >
                  Get Tickets Now
                </a>
              </div>
            </div>
            <p className="text-gray-600 text-sm mt-6">
              Limited tickets available, first come first served. Valid for Xinjiang attractions only.
            </p>
          </div>
        </div>
      </section>

      {/* News Section */}
      <NewsSection 
        website="helloxinjiang"
        limit={10000}
        title="Xinjiang Travel News"
        themeColor="blue"
      />

      {/* Attractions Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Must-See <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Attractions</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Discover the most breathtaking destinations in Xinjiang
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {attractions.map((attraction) => (
              <div key={attraction.id} className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="p-6">
                  <div className="text-4xl mb-4">{attraction.image}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{attraction.title}</h3>
                  <p className="text-gray-600 mb-4">{attraction.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    {attraction.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-gray-700">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Travel <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Tips</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Essential information for your Xinjiang journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {travelTips.map((tip, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-3xl mb-4">{tip.icon}</div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">{tip.title}</h4>
                <p className="text-gray-600">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Touch</span>
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              For travel inquiries and assistance with your Xinjiang journey
            </p>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100">
              <div className="text-4xl mb-4">📧</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact Email</h3>
              <p className="text-gray-700 text-lg mb-6">
                Reach out to us for travel information and assistance
              </p>
              
              <div className="inline-flex items-center justify-center space-x-3 bg-white rounded-xl px-6 py-3.5 shadow-sm">
                <span className="text-blue-500 text-xl">✉️</span>
                <a 
                  href="mailto:info@helloxinjiang.com" 
                  className="text-xl font-semibold text-gray-900 hover:text-blue-600 transition-colors duration-200"
                >
                  info@helloxinjiang.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-lg font-bold">XJ</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">HelloXinjiang</h3>
                  <p className="text-gray-400 text-sm">Xinjiang Travel Guide</p>
                </div>
              </div>
              <p className="text-gray-400">
                Your comprehensive guide to discovering the beauty and culture of Xinjiang, China.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Contact</h4>
              <div className="space-y-4 text-gray-400">
                <div className="flex items-center space-x-3">
                  <span>📧</span>
                  <a href="mailto:info@helloxinjiang.com" className="hover:text-white transition-colors duration-200">
                    info@helloxinjiang.com
                  </a>
                </div>
                <p className="text-sm text-gray-500">
                  For travel information and assistance with your Xinjiang journey
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Explore More Destinations</h4>
              <ul className="space-y-3">
                {destinations.map((destination) => (
                  <li key={destination.name}>
                    <a 
                      href={destination.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      <span className="text-lg">{destination.emoji}</span>
                      <span>
                        <span className="font-medium">{destination.name}</span>
                        <span className="block text-sm text-gray-500">{destination.description}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© 2026 HelloXinjiang. All rights reserved. | Xinjiang Travel Information Portal</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}

export default App